import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { showModal, hideModal, installModalFallback } from "./Modalhelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./Documents.css";

const baseUrl = process.env.REACT_APP_BASE_URL;

// Routes de DocumentController (api/ged/documents) et FolderController (api/ged/folders)
const API = {
    documents: `${baseUrl}/api/ged/documents`,
    documentById: (id) => `${baseUrl}/api/ged/documents/${id}`,
    documentCategory: (id) => `${baseUrl}/api/ged/documents/${id}/category`,
    upload: `${baseUrl}/api/ged/documents/upload`,
    uploadCommercial: `${baseUrl}/api/ged/documents/upload-commercial`,
    download: (id) => `${baseUrl}/api/ged/documents/${id}/download`,
    preview: (id) => `${baseUrl}/api/ged/documents/${id}/preview`,
    newVersion: (id) => `${baseUrl}/api/ged/documents/${id}/version`,
    archive: (id) => `${baseUrl}/api/ged/documents/${id}/archive`,
    restore: (id) => `${baseUrl}/api/ged/documents/${id}/restore`,
    permanent: (id) => `${baseUrl}/api/ged/documents/${id}/permanent`,
    folders: `${baseUrl}/api/ged/folders`,
    commercial: (id) => `${baseUrl}/api/CommercialDocumentVentes/${id}`,
    commercialList: `${baseUrl}/api/CommercialDocumentVentes`,
    clients: `${baseUrl}/api/Clients`,
    suppliers: `${baseUrl}/api/Suppliers`,
    projets: `${baseUrl}/api/Projets`,
    documentProjets: `${baseUrl}/api/DocumentProjets`,
    documentProjet: (id) => `${baseUrl}/api/DocumentProjets/${id}`,
    documentEmployes: `${baseUrl}/api/DocumentEmployes`,
    documentEmploye: (id) => `${baseUrl}/api/DocumentEmployes/${id}`,
    employes: `${baseUrl}/api/Employes`,
};

// L'upload direct d'un document commercial n'existe pas côté API : on modifie seulement les infos des documents existants
const COMMERCIAL_UPLOAD_ENABLED = false;

// Enum DocumentStatus de l'API : Active = 1, Archived = 2, Deleted = 3
// (accepte la valeur numérique ou le nom, selon la sérialisation JSON)
const STATUS = {
    archived: ["Archived", 2],
    deleted: ["Deleted", 3],
};
const isStatus = (doc, key) => STATUS[key].includes(doc.status);

const STATUS_LABEL = (doc) =>
    isStatus(doc, "archived") ? { text: "Archivé", css: "badge-secondary" }
        : isStatus(doc, "deleted") ? { text: "Supprimé", css: "badge-danger" }
            : { text: "Actif", css: "badge-success" };

// Enum CommercialDocumentType de l'API (valeur numérique + nom, l'API peut renvoyer l'un ou l'autre)
const DOC_TYPES = [
    { value: 1, name: "Devis", label: "Devis", css: "badge-info" },
    { value: 2, name: "BonCommande", label: "Bon de commande", css: "badge-primary" },
    { value: 3, name: "BonLivraison", label: "Bon de livraison", css: "badge-warning" },
    { value: 4, name: "FactureVente", label: "Facture de vente", css: "badge-success" },
    { value: 5, name: "FactureAchat", label: "Facture d'achat", css: "badge-dark" },
    { value: 6, name: "Avoir", label: "Avoir", css: "badge-danger" },
];
const findType = (t) =>
    t === null || t === undefined || t === "" ? null
        : DOC_TYPES.find((x) => x.value === Number(t) || x.name === t) || null;

// Un document est commercial s'il porte un type de l'enum ou le discriminateur "Vente"
// Catégories d'informations complémentaires (enum DocumentCategory de l'API).
// Toutes les catégories peuvent être choisies pour CLASSER un document.
// hasForm: false → le formulaire « Compléter les informations » n'existe pas encore (bientôt).
const DOC_CATEGORIES = [
    { value: 1, name: "Vente", label: "Vente", icon: "fa-file-invoice-dollar", color: "text-success", hasForm: true, css: "badge-success",
      hint: "Facture, bon de livraison, devis… avec montants et client" },
    { value: 2, name: "Achat", label: "Achat", icon: "fa-shopping-cart", color: "text-primary", hasForm: true, css: "badge-primary",
      hint: "Facture fournisseur, bon de réception…" },
    { value: 3, name: "RH", label: "Ressources humaines", icon: "fa-user-tie", color: "text-info", hasForm: true, css: "badge-info",
      hint: "Contrat, fiche de paie, attestation…" },
    { value: 4, name: "Projet", label: "Projet", icon: "fa-hard-hat", color: "text-warning", hasForm: true, css: "badge-warning",
      hint: "Photo, vidéo, plan, PV… d'un projet / réalisation" },
];
const PROJET_CATEGORY = 4;
const RH_CATEGORY = 3;
const TYPES_RH = [
    { value: 1, name: "Contrat",       label: "Contrat de travail", fields: ["intitule", "dateDocument", "dateExpiration"] },
    { value: 2, name: "Avenant",       label: "Avenant",            fields: ["intitule", "dateDocument"] },
    { value: 3, name: "BulletinPaie",  label: "Bulletin de paie",   fields: ["periode"] },
    { value: 4, name: "Diplome",       label: "Diplôme",            fields: ["intitule", "organisme", "dateDocument"] },
    { value: 5, name: "Certificat",    label: "Certificat / formation", fields: ["intitule", "organisme", "dateDocument", "dateExpiration"] },
    { value: 6, name: "PieceIdentite", label: "Pièce d'identité",   fields: ["intitule", "dateDocument", "dateExpiration"] },
    { value: 7, name: "Attestation",   label: "Attestation",        fields: ["intitule", "dateDocument"] },
    { value: 8, name: "Conge",         label: "Congé / absence",    fields: ["dateDocument", "dateExpiration"] },
    { value: 9, name: "Evaluation",    label: "Évaluation",         fields: ["dateDocument"] },
    { value: 10, name: "Autre",        label: "Autre",              fields: ["intitule", "dateDocument"] },
];
const findTypeRH = (t) => TYPES_RH.find((x) => x.value === Number(t) || x.name === t) || TYPES_RH[9];
const INTITULE_PLACEHOLDER = { 1: "Ex. : CDI, CDD 12 mois…", 4: "Ex. : Licence en informatique", 5: "Ex. : Habilitation électrique", 6: "Ex. : CIN, passeport", 7: "Ex. : Attestation de travail" };
const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const hasRhInfos = (d) => !!d.employeId;

const rhLinkSchema = Yup.object({
    employeId: Yup.string().required("Sélectionnez l'employé"),
    type: Yup.string().required("Sélectionnez le type de document"),
    periodeMois: Yup.string().when("type", { is: "3", then: (s) => s.required("Mois obligatoire"), otherwise: (s) => s.nullable() }),
    periodeAnnee: Yup.string().when("type", { is: "3", then: (s) => s.required("Année obligatoire"), otherwise: (s) => s.nullable() }),
    intitule: Yup.string().nullable().max(200, "200 caractères maximum"),
    organisme: Yup.string().nullable().max(200, "200 caractères maximum"),
    commentaire: Yup.string().nullable().max(1000, "1000 caractères maximum"),
});
const findCategory = (c) =>
    c === null || c === undefined || c === "" || c === 0 || c === "Aucune" ? null
        : DOC_CATEGORIES.find((x) => x.value === Number(c) || x.name === c) || null;

// Configuration des catégories commerciales : endpoints, types autorisés, libellés
const COMMERCIAL_FORMS = {
    1: {
        list: `${baseUrl}/api/CommercialDocumentVentes`,
        item: (id) => `${baseUrl}/api/CommercialDocumentVentes/${id}`,
        allowedTypes: [1, 2, 3, 4, 6],
        typeLabels: {},
        party: "Client",
        partyField: "clientId",        // champ envoyé à l'API
        partyList: "clients",          // clé de la liste chargée (api/Clients)
        infosLabel: "Infos de vente",
        of: "de vente",
        icon: "fa-file-invoice-dollar",
    },
    2: {
        list: `${baseUrl}/api/CommercialDocumentAchats`,
        item: (id) => `${baseUrl}/api/CommercialDocumentAchats/${id}`,
        allowedTypes: [1, 2, 3, 5, 6],
        typeLabels: { 1: "Demande de prix", 3: "Bon de réception" },
        party: "Fournisseur",
        partyField: "supplierId",
        partyList: "suppliers",        // api/Suppliers
        infosLabel: "Infos d'achat",
        of: "d'achat",
        icon: "fa-shopping-cart",
    },
};

// Types proposés pour une catégorie (filtrés et renommés : ex. « Bon de réception » pour un achat)
const typesFor = (categoryValue) => {
    const form = COMMERCIAL_FORMS[categoryValue];
    if (!form) return DOC_TYPES;
    return DOC_TYPES
        .filter((t) => form.allowedTypes.includes(t.value))
        .map((t) => ({ ...t, label: form.typeLabels[t.value] || t.label }));
};

// Le document a-t-il déjà ses infos commerciales (ligne dans document_ventes / document_achats) ?
const hasCommercialInfos = (d) => !!d.commercialCategory || !!d.isCommercial || !!findType(d.type);
// Le document est-il rattaché à un projet (ligne dans document_projets) ?
const hasProjetInfos = (d) => !!d.projetId;
const hasVenteInfos = hasCommercialInfos;   // compatibilité

// Catégorie effective : celle renvoyée par l'API, sinon déduite des infos commerciales
const docCategory = (d) =>
    findCategory(d.category) || findCategory(d.commercialCategory)
    || (hasProjetInfos(d) ? findCategory(PROJET_CATEGORY) : null)
    || (hasRhInfos(d) ? findCategory(RH_CATEGORY) : null)
    || (hasCommercialInfos(d) ? DOC_CATEGORIES[0] : null);

// Libellé du type selon la catégorie du document
const typeLabelFor = (d) => {
    const t = findType(d.type);
    if (!t) return null;
    const form = COMMERCIAL_FORMS[docCategory(d)?.value];
    return { ...t, label: form?.typeLabels[t.value] || t.label };
};

const isCommercialDoc = (d) => hasCommercialInfos(d);

const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");

// Champ de formulaire générique (Formik + Bootstrap)
const renderField = (formik, name, label, props = {}) => (
    <>
        <label>{label}</label>
        <input
            name={name}
            className={`form-control ${formik.touched[name] && formik.errors[name] ? "is-invalid" : ""}`}
            value={formik.values[name] ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            {...props}
        />
        <div className="invalid-feedback">{formik.errors[name]}</div>
    </>
);

const formatAmount = (v) =>
    v === null || v === undefined || v === "" ? ""
        : Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const unwrap = (res) => (res.data && res.data.data !== undefined ? res.data.data : res.data);
const apiMessage = (error, fallback) =>
    error.response?.data?.message || error.response?.data?.Message || fallback;

const toast = (icon, title) =>
    Swal.fire({ toast: true, position: "top-end", icon, title, showConfirmButton: false, timer: 2000 });

const formatSize = (bytes) => {
    if (bytes === null || bytes === undefined || bytes === "") return "";
    const units = ["o", "Ko", "Mo", "Go"];
    let size = Number(bytes);
    let i = 0;
    while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const fileIcon = (name = "") => {
    const ext = name.split(".").pop().toLowerCase();
    if (ext === "pdf") return "fa-file-pdf text-danger";
    if (["doc", "docx"].includes(ext)) return "fa-file-word text-primary";
    if (["xls", "xlsx", "csv"].includes(ext)) return "fa-file-excel text-success";
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext)) return "fa-file-image text-info";
    if (["zip", "rar", "7z"].includes(ext)) return "fa-file-archive text-warning";
    return "fa-file-alt text-secondary";
};

// Construit le chemin complet d'un dossier : "Racine / Sous-dossier"
const folderPath = (folders, id) => {
    const parts = [];
    let current = folders.find((f) => f.id === id);
    while (current) {
        parts.unshift(current.name);
        current = folders.find((f) => f.id === current.parentId);
    }
    return parts.join(" / ");
};

const isCommercial = (kind) => kind === "commercial";
const amount = (label) =>
    Yup.number().typeError(`${label} invalide`).min(0, `${label} invalide`);

// Liste plate (id / parentId) → arbre
const buildFolderTree = (folders) => {
    const map = {};
    folders.forEach((f) => (map[f.id] = { ...f, children: [] }));
    const roots = [];
    folders.forEach((f) => {
        if (f.parentId && map[f.parentId]) map[f.parentId].children.push(map[f.id]);
        else roots.push(map[f.id]);
    });
    const sort = (nodes) => {
        nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
        nodes.forEach((n) => sort(n.children));
    };
    sort(roots);
    return roots;
};

const FolderTreeNode = ({ node, selectedId, expanded, onToggle, onSelect, counts }) => {
    const isOpen = expanded.has(node.id);
    const hasChildren = node.children.length > 0;
    const count = counts[node.id] || 0;
    return (
        <li>
            <div
                className={`doc-tree-node ${selectedId === node.id ? "active" : ""}`}
                onClick={() => onSelect(node.id)}
                title={node.name}
            >
                <span
                    className="doc-tree-toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasChildren) onToggle(node.id);
                    }}
                >
                    {hasChildren ? <i className={`fas fa-caret-${isOpen ? "down" : "right"}`}></i> : null}
                </span>
                <i className={`fas fa-folder${isOpen || selectedId === node.id ? "-open" : ""} text-warning mr-2`}></i>
                <span className="doc-tree-name">{node.name}</span>
                {count > 0 && <span className="badge badge-light ml-auto">{count}</span>}
            </div>
            {hasChildren && isOpen && (
                <ul className="doc-tree">
                    {node.children.map((c) => (
                        <FolderTreeNode key={c.id} node={c} selectedId={selectedId}
                            expanded={expanded} onToggle={onToggle} onSelect={onSelect} counts={counts} />
                    ))}
                </ul>
            )}
        </li>
    );
};

const uploadSchema = Yup.object({
    kind: Yup.string().oneOf(["simple", "commercial"]),
    folderId: Yup.string().required("Le dossier est obligatoire"),
    files: Yup.array()
        .min(1, "Sélectionnez au moins un fichier")
        .when("kind", {
            is: isCommercial,
            then: (s) => s.max(1, "Un seul fichier par document commercial"),
        }),
    description: Yup.string().nullable(),
    type: Yup.string().when("kind", { is: isCommercial, then: (s) => s.required("Le type est obligatoire") }),
    numero: Yup.string().when("kind", { is: isCommercial, then: (s) => s.trim().required("Le numéro est obligatoire") }),
    dateDocument: Yup.string().when("kind", { is: isCommercial, then: (s) => s.required("La date est obligatoire") }),
    montantHT: amount("Montant HT").when("kind", { is: isCommercial, then: (s) => s.required("Montant HT obligatoire") }),
    montantTVA: amount("Montant TVA").when("kind", { is: isCommercial, then: (s) => s.required("Montant TVA obligatoire") }),
    montantTTC: amount("Montant TTC").when("kind", { is: isCommercial, then: (s) => s.required("Montant TTC obligatoire") }),
    ice: Yup.string().nullable().matches(/^\d{15}$/, { message: "L'ICE comporte 15 chiffres", excludeEmptyString: true }),
});

const commercialSchema = Yup.object({
    type: Yup.string().required("Le type est obligatoire"),
    numero: Yup.string().trim().required("Le numéro est obligatoire"),
    dateDocument: Yup.string().required("La date est obligatoire"),
    montantHT: amount("Montant HT").required("Montant HT obligatoire"),
    montantTVA: amount("Montant TVA").required("Montant TVA obligatoire"),
    montantTTC: amount("Montant TTC").required("Montant TTC obligatoire"),
    partyId: Yup.string().required("Sélectionnez le client / fournisseur"),
});

const projetLinkSchema = Yup.object({
    projetId: Yup.string().required("Sélectionnez le projet"),
    commentaire: Yup.string().nullable().max(1000, "1000 caractères maximum"),
});

const commercialInitialValues = {
    type: "",
    numero: "",
    dateDocument: "",
    montantHT: "",
    montantTVA: "",
    montantTTC: "",
    partyId: "",
};

const uploadInitialValues = {
    kind: "simple",
    folderId: "",
    files: [],
    description: "",
    type: "",
    numero: "",
    dateDocument: "",
    montantHT: "",
    montantTVA: "",
    montantTTC: "",
    codeClient: "",
    raisonSociale: "",
    adresse: "",
    ville: "",
    telephone: "",
    ice: "",
};

const editSchema = Yup.object({
    fileName: Yup.string().trim().required("Le nom est obligatoire").max(255, "255 caractères maximum"),
    description: Yup.string().nullable(),
});

// defaultStatus : "active" (par défaut), "archived" ou "deleted" (Corbeille)
const Documents = ({ defaultStatus = "active" }) => {
    const isTrash = defaultStatus === "deleted";
    const [documents, setDocuments] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    // Filtres
    const [search, setSearch] = useState("");
    const [folderFilter, setFolderFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState(defaultStatus);
    const [typeFilter, setTypeFilter] = useState("");   // "" = tous, "simple", ou valeur de l'enum
    const [categoryFilter, setCategoryFilter] = useState("");   // "" = toutes, "none" = non classés, ou valeur de l'enum
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [expanded, setExpanded] = useState(new Set());

    const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

    const selectedFolderPath = folderFilter ? folderPath(folders, folderFilter) : "";

    // Nombre de documents par dossier, selon le statut affiché (badge dans l'arbre)
    const docCountByFolder = useMemo(() => {
        const counts = {};
        documents.forEach((d) => {
            if (statusFilter === "active" && (isStatus(d, "archived") || isStatus(d, "deleted"))) return;
            if (statusFilter === "archived" && !isStatus(d, "archived")) return;
            if (statusFilter === "deleted" && !isStatus(d, "deleted")) return;
            if (statusFilter === "all" && isStatus(d, "deleted")) return;
            counts[d.folderId] = (counts[d.folderId] || 0) + 1;
        });
        return counts;
    }, [documents, statusFilter]);

    const toggleFolder = (id) => {
        const next = new Set(expanded);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpanded(next);
    };

    const selectFolder = (id) => {
        setFolderFilter(id);
        // ouvre le dossier sélectionné pour voir ses sous-dossiers
        const next = new Set(expanded);
        next.add(id);
        setExpanded(next);
    };

    useEffect(() => {
        fetchDocuments();
        fetchFolders();
    }, []);

    // Popups utilisables même sans jQuery / Bootstrap JS
    useEffect(() => installModalFallback(), []);

    // Même composant pour Recherche et Corbeille : on resynchronise le filtre au changement de menu
    useEffect(() => {
        setStatusFilter(defaultStatus);
    }, [defaultStatus]);

    // Charge les documents GED + les infos commerciales, puis les fusionne par Id
    // (TPT : un document commercial a le même Id dans documents et commercialdocuments)
    const fetchDocuments = async () => {
        try {
            const [docsRes, ventesRes, achatsRes, projetsRes, rhRes] = await Promise.all([
                axiosInstance.get(API.documents),
                axiosInstance.get(COMMERCIAL_FORMS[1].list).catch(() => null), // la GED reste utilisable si ces appels échouent
                axiosInstance.get(COMMERCIAL_FORMS[2].list).catch(() => null),
                axiosInstance.get(API.documentProjets).catch(() => null),
                axiosInstance.get(API.documentEmployes).catch(() => null),
            ]);

            // Normalise la catégorie, quel que soit le nom/format renvoyé par l'API
            // (category, Category, documentCategory ; valeur 1 ou "Vente")
            const docs = (unwrap(docsRes) || []).map((d) => ({
                ...d,
                category: d.category ?? d.Category ?? d.documentCategory ?? d.DocumentCategory ?? null,
                createdAt: d.createdAt ?? d.uploadedAt ?? null,   // le DTO de l'API renvoie uploadedAt
            }));

            const byId = {};
            [[ventesRes, 1], [achatsRes, 2]].forEach(([res, cat]) => {
                const list = res ? unwrap(res) || [] : [];
                list.forEach((c) => { if (c?.id) byId[String(c.id)] = { ...c, _category: cat }; });
            });
            const projetById = {};
            (projetsRes ? unwrap(projetsRes) || [] : []).forEach((p) => { if (p?.id) projetById[String(p.id)] = p; });
            const rhById = {};
            (rhRes ? unwrap(rhRes) || [] : []).forEach((r) => { if (r?.id) rhById[String(r.id)] = r; });

            setDocuments(docs.map((d) => {
                const p = projetById[String(d.id)];
                if (p) return { ...d, projetId: p.projetId, projetNom: p.projetNom, commentaire: p.commentaire };
                const r = rhById[String(d.id)];
                if (r) return {
                    ...d,
                    employeId: r.employeId, employeNom: r.employeNom, matricule: r.matricule,
                    rhType: r.type, rhDateDocument: r.dateDocument, rhDateExpiration: r.dateExpiration, rhExpire: r.expire,
                    periodeMois: r.periodeMois, periodeAnnee: r.periodeAnnee,
                    intitule: r.intitule, organisme: r.organisme, commentaire: r.commentaire,
                };
                const c = byId[String(d.id)];
                if (!c) return d;
                return {
                    ...d,
                    isCommercial: true,
                    commercialCategory: c._category,
                    type: c.type ?? d.type,
                    numero: c.numero ?? d.numero,
                    dateDocument: c.dateDocument ?? d.dateDocument,
                    montantHT: c.montantHT ?? d.montantHT,
                    montantTVA: c.montantTVA ?? d.montantTVA,
                    montantTTC: c.montantTTC ?? d.montantTTC,
                    // Client (vente) ou fournisseur (achat)
                    partyId: c.clientId ?? c.supplierId ?? null,
                    partyName: c.clientName ?? c.supplierName ?? null,
                    partyPhone: c.clientPhone ?? c.supplierPhone ?? null,
                };
            }));
        } catch (error) {
            toast("error", apiMessage(error, "Impossible de charger les documents"));
        } finally {
            setLoading(false);
        }
    };

    const fetchFolders = () => {
        axiosInstance.get(API.folders)
            .then((res) => setFolders(unwrap(res) || []))
            .catch(() => setFolders([]));
    };

    // Dossiers en arbre (parcours en profondeur), comme dans l'Explorateur :
    // 📁 Ventes
    //    └ 📁 Factures
    //       └ 📁 2026
    const folderOptions = useMemo(() => {
        const byParent = {};
        folders.forEach((f) => {
            const key = f.parentId || "__root__";
            (byParent[key] = byParent[key] || []).push(f);
        });
        Object.values(byParent).forEach((list) =>
            list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
        );

        const ids = new Set(folders.map((f) => f.id));
        const result = [];
        const visit = (folder, depth) => {
            const indent = "   ".repeat(depth);       // espaces insécables : visibles dans un <option>
            result.push({
                id: folder.id,
                label: `${indent}${depth > 0 ? "└ " : ""}📁 ${folder.name}`,
            });
            (byParent[folder.id] || []).forEach((child) => visit(child, depth + 1));
        };

        // Racines = dossiers sans parent, ou dont le parent est introuvable
        folders
            .filter((f) => !f.parentId || !ids.has(f.parentId))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
            .forEach((root) => visit(root, 0));

        return result;
    }, [folders]);

    const filteredDocuments = useMemo(() => {
        const term = search.trim().toLowerCase();
        return documents.filter((d) => {
            if (statusFilter === "active" && (isStatus(d, "archived") || isStatus(d, "deleted"))) return false;
            if (statusFilter === "archived" && !isStatus(d, "archived")) return false;
            if (statusFilter === "deleted" && !isStatus(d, "deleted")) return false;
            if (statusFilter === "all" && isStatus(d, "deleted")) return false; // la corbeille reste à part
            if (folderFilter && d.folderId !== folderFilter) return false;

            const cat = docCategory(d);
            if (categoryFilter === "none" && cat) return false;
            if (categoryFilter && categoryFilter !== "none" && cat?.value !== Number(categoryFilter)) return false;

            const docType = findType(d.type);
            if (typeFilter === "simple" && docType) return false;
            if (typeFilter && typeFilter !== "simple" && docType?.value !== Number(typeFilter)) return false;

            if (dateFrom || dateTo) {
                const raw = d.dateDocument || d.createdAt;
                const day = raw ? String(raw).substring(0, 10) : "";
                if (!day) return false;
                if (dateFrom && day < dateFrom) return false;
                if (dateTo && day > dateTo) return false;
            }

            if (term) {
                const haystack = [
                    d.fileName, d.description, d.numero,
                    d.partyName, d.partyPhone, d.projetNom, d.commentaire,
                    d.employeNom, d.matricule, d.intitule, d.organisme,
                ].filter(Boolean).join(" ").toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            return true;
        });
    }, [documents, search, folderFilter, statusFilter, categoryFilter, typeFilter, dateFrom, dateTo]);

    // ---------- Upload ----------
    const uploadFormik = useFormik({
        initialValues: uploadInitialValues,
        validationSchema: uploadSchema,
        onSubmit: async (values, { resetForm }) => {
            setUploading(true);
            let success = 0;
            const failures = [];
            const commercial = isCommercial(values.kind);

            for (const file of values.files) {
                const formData = new FormData();
                formData.append("file", file);              // ⚠️ noms à aligner sur UploadDocumentDto
                formData.append("folderId", values.folderId);
                if (values.description) formData.append("description", values.description);

                if (commercial) {
                    // Champs de UploadCommercialDocumentDto
                    formData.append("type", values.type);
                    formData.append("numero", values.numero.trim());
                    formData.append("dateDocument", values.dateDocument);
                    formData.append("montantHT", values.montantHT);
                    formData.append("montantTVA", values.montantTVA);
                    formData.append("montantTTC", values.montantTTC);
                    ["codeClient", "raisonSociale", "adresse", "ville", "telephone", "ice"].forEach((k) => {
                        if (values[k]) formData.append(k, values[k].trim());
                    });
                }

                try {
                    await axiosInstance.post(commercial ? API.uploadCommercial : API.upload, formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    success++;
                } catch (error) {
                    failures.push(`${file.name} : ${apiMessage(error, "erreur")}`);
                }
            }
            setUploading(false);
            fetchDocuments();

            if (failures.length === 0) {
                hideModal("uploadModal");
                resetForm();
                toast("success", success > 1 ? `${success} documents ajoutés` : "Document ajouté");
            } else {
                Swal.fire({
                    icon: success ? "warning" : "error",
                    title: `${success} réussi(s), ${failures.length} échec(s)`,
                    html: failures.map((f) => `<div class="text-left small">${f}</div>`).join(""),
                });
            }
        },
    });

    const openUpload = () => {
        uploadFormik.resetForm({ values: { ...uploadInitialValues, folderId: folderFilter || "" } });
        showModal("uploadModal");
    };

    // Calcule le TTC automatiquement quand HT ou TVA change
    const handleAmountChange = (e) => {
        uploadFormik.handleChange(e);
        const { name, value } = e.target;
        const ht = name === "montantHT" ? value : uploadFormik.values.montantHT;
        const tva = name === "montantTVA" ? value : uploadFormik.values.montantTVA;
        if (ht !== "" && tva !== "" && !isNaN(ht) && !isNaN(tva)) {
            uploadFormik.setFieldValue("montantTTC", (parseFloat(ht) + parseFloat(tva)).toFixed(2));
        }
    };

    const fieldClass = (name) =>
        `form-control ${uploadFormik.touched[name] && uploadFormik.errors[name] ? "is-invalid" : ""}`;

    const uploadField = (name, label, props = {}) => (
        <>
            <label>{label}</label>
            <input
                name={name}
                className={fieldClass(name)}
                value={uploadFormik.values[name]}
                onChange={uploadFormik.handleChange}
                onBlur={uploadFormik.handleBlur}
                {...props}
            />
            <div className="invalid-feedback">{uploadFormik.errors[name]}</div>
        </>
    );

    // ---------- Modification ----------
    const editFormik = useFormik({
        initialValues: { fileName: "", description: "" },
        validationSchema: editSchema,
        onSubmit: async (values, { setErrors }) => {
            try {
                await axiosInstance.put(API.documentById(selectedDocument.id), {   // ⚠️ champs de UpdateDocumentDto
                    fileName: values.fileName,
                    description: values.description || null,
                });
                hideModal("editModal");
                toast("success", "Document modifié");
                fetchDocuments();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Modification échouée"));
            }
        },
    });

    const openEdit = (doc) => {
        setSelectedDocument(doc);
        editFormik.setValues({ fileName: doc.fileName || "", description: doc.description || "" });
        showModal("editModal");
    };

    // ---------- Informations commerciales ----------
    const [commercialDoc, setCommercialDoc] = useState(null);
    const [commercialLoading, setCommercialLoading] = useState(false);
    const [commercialMode, setCommercialMode] = useState("edit");   // "create" = compléter, "edit" = modifier
    const [commercialCategory, setCommercialCategory] = useState(1);  // 1 = Vente, 2 = Achat
    const commercialForm = COMMERCIAL_FORMS[commercialCategory] || COMMERCIAL_FORMS[1];
    const [parties, setParties] = useState({ clients: null, suppliers: null });   // listes déroulantes

    // Charge (une seule fois) la liste des clients ou des fournisseurs
    const loadParties = async (category) => {
        const form = COMMERCIAL_FORMS[category] || COMMERCIAL_FORMS[1];
        const key = form.partyList;
        if (parties[key]) return;
        try {
            const res = await axiosInstance.get(API[key]);
            const list = (unwrap(res) || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setParties((prev) => ({ ...prev, [key]: list }));
        } catch (error) {
            toast("error", apiMessage(error, `Impossible de charger la liste des ${form.party.toLowerCase()}s`));
        }
    };

    const commercialFormik = useFormik({
        initialValues: commercialInitialValues,
        validationSchema: commercialSchema,
        onSubmit: async (values, { setErrors }) => {
            // Champs de CommercialDocumentVentes/AchatsCreateDto / UpdateDto
            const payload = {
                type: Number(values.type),
                numero: values.numero.trim(),
                dateDocument: values.dateDocument,
                montantHT: parseFloat(values.montantHT),
                montantTVA: parseFloat(values.montantTVA),
                montantTTC: parseFloat(values.montantTTC),
                [commercialForm.partyField]: Number(values.partyId),   // clientId ou supplierId
            };
            try {
                if (commercialMode === "create") {
                    // POST : rattache les infos au document existant
                    await axiosInstance.post(commercialForm.list, { ...payload, documentId: commercialDoc.id });
                } else {
                    await axiosInstance.put(commercialForm.item(commercialDoc.id), { ...payload, id: commercialDoc.id });
                }
                hideModal("commercialModal");
                toast("success", commercialMode === "create"
                    ? `Document complété : informations ${commercialForm.of} ajoutées`
                    : `Informations ${commercialForm.of} modifiées`);
                fetchDocuments();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const partyOptions = parties[commercialForm.partyList] || [];
    const selectedParty = partyOptions.find((x) => String(x.id) === String(commercialFormik.values.partyId));

    const fillCommercialForm = (data) => {
        commercialFormik.resetForm({
            values: {
                type: findType(data.type)?.value ?? "",
                numero: data.numero ?? "",
                dateDocument: toDateInput(data.dateDocument),
                montantHT: data.montantHT ?? "",
                montantTVA: data.montantTVA ?? "",
                montantTTC: data.montantTTC ?? "",
                partyId: data.clientId ?? data.supplierId ?? data.partyId ?? "",
            },
        });
    };

    // Compléter un document simple : formulaire vide (date du jour par défaut)
    const openCommercialCreate = (doc) => {
        setCommercialCategory(docCategory(doc)?.value || 1);
        loadParties(docCategory(doc)?.value || 1);
        setCommercialMode("create");
        setCommercialDoc(doc);
        commercialFormik.resetForm({
            values: { ...commercialInitialValues, dateDocument: new Date().toISOString().substring(0, 10) },
        });
        setCommercialLoading(false);
        showModal("commercialModal");
    };

    // Retirer les infos commerciales : le document redevient simple, le fichier est conservé
    const handleRemoveCommercial = (doc) => {
        const form = COMMERCIAL_FORMS[docCategory(doc)?.value] || COMMERCIAL_FORMS[1];
        Swal.fire({
            title: `Retirer les informations ${form.of} de « ${doc.fileName} » ?`,
            text: `Le document et son fichier sont conservés ; seules les informations ${form.of} sont supprimées.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Retirer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axiosInstance.delete(form.item(doc.id));
                toast("success", `Informations ${form.of} retirées`);
                fetchDocuments();
            } catch (error) {
                toast("error", apiMessage(error, "Opération échouée"));
            }
        });
    };

    // ---------- Choix de la catégorie (étape 1 de « Compléter les informations ») ----------
    const [categoryDoc, setCategoryDoc] = useState(null);

    const openCategoryPicker = (doc) => {
        setCategoryDoc(doc);
        showModal("categoryModal");
    };

    const [savingCategory, setSavingCategory] = useState(false);

    // Étape 1 : classer le document (Category en base), sans encore saisir ses infos
    const chooseCategory = async (category) => {
        if (!categoryDoc || savingCategory) return;
        if (docCategory(categoryDoc)?.value === category.value) {
            hideModal("categoryModal");
            return;
        }
        setSavingCategory(true);
        try {
            await axiosInstance.put(API.documentCategory(categoryDoc.id), { category: category.value });
            hideModal("categoryModal");
            toast("success", `Document classé : ${category.label}`);
            fetchDocuments();
        } catch (error) {
            // 409 : le document a déjà des infos d'une autre catégorie
            Swal.fire({ icon: "error", title: "Classement impossible", text: apiMessage(error, "Opération échouée") });
        } finally {
            setSavingCategory(false);
        }
    };

    const openCommercial = async (doc) => {
        const category = docCategory(doc)?.value || 1;
        setCommercialCategory(category);
        loadParties(category);
        setCommercialMode("edit");
        setCommercialDoc(doc);
        fillCommercialForm(doc);                 // affiche tout de suite ce que la liste connaît déjà
        showModal("commercialModal");
        setCommercialLoading(true);
        try {
            const res = await axiosInstance.get((COMMERCIAL_FORMS[category] || COMMERCIAL_FORMS[1]).item(doc.id));
            const data = unwrap(res);
            if (data) fillCommercialForm(data); // puis complète avec la fiche complète
        } catch (error) {
            toast("error", apiMessage(error, "Impossible de charger les informations commerciales"));
        } finally {
            setCommercialLoading(false);
        }
    };

    const handleCommercialAmountChange = (e) => {
        commercialFormik.handleChange(e);
        const { name, value } = e.target;
        const ht = name === "montantHT" ? value : commercialFormik.values.montantHT;
        const tva = name === "montantTVA" ? value : commercialFormik.values.montantTVA;
        if (ht !== "" && tva !== "" && !isNaN(ht) && !isNaN(tva)) {
            commercialFormik.setFieldValue("montantTTC", (parseFloat(ht) + parseFloat(tva)).toFixed(2));
        }
    };

    // ---------- Rattachement à un projet ----------
    const [projetDoc, setProjetDoc] = useState(null);
    const [projetMode, setProjetMode] = useState("create");     // "create" = rattacher, "edit" = modifier
    const [projets, setProjets] = useState(null);
    const [projetHint, setProjetHint] = useState(null);          // projet repris du dernier document du dossier

    const loadProjets = async () => {
        try {
            const res = await axiosInstance.get(API.projets);
            setProjets(unwrap(res) || []);
        } catch (error) {
            setProjets([]);
            toast("error", apiMessage(error, "Impossible de charger les projets"));
        }
    };

    const projetFormik = useFormik({
        initialValues: { projetId: "", commentaire: "" },
        validationSchema: projetLinkSchema,
        onSubmit: async (values, { setErrors }) => {
            const payload = { projetId: Number(values.projetId), commentaire: values.commentaire?.trim() || null };
            try {
                if (projetMode === "create") {
                    await axiosInstance.post(API.documentProjets, { ...payload, documentId: projetDoc.id });
                } else {
                    await axiosInstance.put(API.documentProjet(projetDoc.id), payload);
                }
                hideModal("projetLinkModal");
                toast("success", projetMode === "create" ? "Document rattaché au projet" : "Informations projet modifiées");
                fetchDocuments();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openProjetLink = (doc) => {
        const editing = hasProjetInfos(doc);
        setProjetDoc(doc);
        setProjetMode(editing ? "edit" : "create");
        loadProjets();

        // Nouveau rattachement : on propose le projet du dernier document rattaché dans le même dossier
        let hint = null;
        if (!editing) {
            hint = documents
                .filter((x) => x.id !== doc.id && x.folderId === doc.folderId && hasProjetInfos(x))
                .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")))[0] || null;
        }
        setProjetHint(hint);
        projetFormik.resetForm({
            values: {
                projetId: editing ? String(doc.projetId) : hint ? String(hint.projetId) : "",
                commentaire: editing ? doc.commentaire || "" : "",
            },
        });
        showModal("projetLinkModal");
    };

    const handleRemoveProjet = (doc) => {
        Swal.fire({
            title: `Retirer « ${doc.fileName} » du projet ${doc.projetNom ? `« ${doc.projetNom} »` : ""} ?`,
            text: "Le document et son fichier sont conservés ; seul le rattachement au projet est supprimé.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Retirer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axiosInstance.delete(API.documentProjet(doc.id));
                toast("success", "Document retiré du projet");
                fetchDocuments();
            } catch (error) {
                toast("error", apiMessage(error, "Opération échouée"));
            }
        });
    };

    // ---------- Documents RH (dossier employé) ----------
    const [rhDoc, setRhDoc] = useState(null);
    const [rhMode, setRhMode] = useState("create");
    const [employes, setEmployes] = useState(null);

    const loadEmployes = () => {
        if (employes) return;
        axiosInstance.get(API.employes)
            .then((res) => setEmployes((unwrap(res) || []).filter((e) => e.actif !== false)))
            .catch(() => setEmployes([]));
    };

    const rhFormik = useFormik({
        initialValues: {
            employeId: "", type: "", dateDocument: "", dateExpiration: "",
            periodeMois: "", periodeAnnee: "", intitule: "", organisme: "", commentaire: "",
        },
        validationSchema: rhLinkSchema,
        onSubmit: async (v, { setErrors }) => {
            const payload = {
                employeId: Number(v.employeId),
                type: Number(v.type),
                dateDocument: v.dateDocument || null,
                dateExpiration: v.dateExpiration || null,
                periodeMois: v.periodeMois ? Number(v.periodeMois) : null,
                periodeAnnee: v.periodeAnnee ? Number(v.periodeAnnee) : null,
                intitule: v.intitule?.trim() || null,
                organisme: v.organisme?.trim() || null,
                commentaire: v.commentaire?.trim() || null,
            };
            try {
                if (rhMode === "create") await axiosInstance.post(API.documentEmployes, { ...payload, documentId: rhDoc.id });
                else await axiosInstance.put(API.documentEmploye(rhDoc.id), payload);
                hideModal("rhLinkModal");
                toast("success", rhMode === "create" ? "Document classé dans le dossier de l'employé" : "Informations RH modifiées");
                fetchDocuments();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openRhLink = (doc) => {
        const editing = hasRhInfos(doc);
        setRhDoc(doc);
        setRhMode(editing ? "edit" : "create");
        loadEmployes();
        const d10 = (x) => (x ? String(x).substring(0, 10) : "");
        rhFormik.resetForm({
            values: editing ? {
                employeId: String(doc.employeId), type: String(findTypeRH(doc.rhType).value),
                dateDocument: d10(doc.rhDateDocument), dateExpiration: d10(doc.rhDateExpiration),
                periodeMois: doc.periodeMois ? String(doc.periodeMois) : "",
                periodeAnnee: doc.periodeAnnee ? String(doc.periodeAnnee) : "",
                intitule: doc.intitule || "", organisme: doc.organisme || "", commentaire: doc.commentaire || "",
            } : {
                employeId: "", type: "", dateDocument: "", dateExpiration: "",
                periodeMois: String(new Date().getMonth() + 1), periodeAnnee: String(new Date().getFullYear()),
                intitule: "", organisme: "", commentaire: "",
            },
        });
        showModal("rhLinkModal");
    };

    const handleRemoveRh = (doc) => {
        Swal.fire({
            title: `Retirer « ${doc.fileName} » du dossier de ${doc.employeNom || "l'employé"} ?`,
            text: "Le document et son fichier sont conservés ; seules les informations RH sont supprimées.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Retirer",
            cancelButtonText: "Annuler",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axiosInstance.delete(API.documentEmploye(doc.id));
                toast("success", "Document retiré du dossier employé");
                fetchDocuments();
            } catch (error) {
                toast("error", apiMessage(error, "Opération échouée"));
            }
        });
    };

    // Champs du pop-up RH à afficher selon le type choisi
    const rhFields = rhFormik.values.type ? findTypeRH(rhFormik.values.type).fields : [];
    const rhShow = (f) => rhFields.includes(f);

    // ---------- Menu « Plus » (géré par React, sans Bootstrap JS) ----------
    const [actionMenu, setActionMenu] = useState(null);   // { doc, archived, top, right }
    const versionInputRef = useRef(null);
    const [versionDoc, setVersionDoc] = useState(null);

    const toggleActionMenu = (e, doc, archived) => {
        e.stopPropagation();
        if (actionMenu?.doc.id === doc.id) {
            setActionMenu(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const MENU_HEIGHT = 300;                                     // hauteur approximative du menu
        const openUp = rect.bottom + MENU_HEIGHT > window.innerHeight; // pas de place en dessous → on ouvre vers le haut
        setActionMenu({
            doc,
            archived,
            right: window.innerWidth - rect.right,
            top: openUp ? undefined : rect.bottom + 4,
            bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        });
    };

    const runMenuAction = (action) => {
        const doc = actionMenu.doc;
        setActionMenu(null);
        action(doc);
    };

    // Ferme le menu : clic ailleurs, défilement, redimensionnement, touche Échap
    useEffect(() => {
        if (!actionMenu) return;
        const close = () => setActionMenu(null);
        const onKey = (e) => { if (e.key === "Escape") close(); };
        document.addEventListener("click", close);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("click", close);
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
            document.removeEventListener("keydown", onKey);
        };
    }, [actionMenu]);

    // « Nouvelle version » : un seul input fichier, hors du menu (sinon il disparaît avant la sélection)
    const pickNewVersion = (doc) => {
        setVersionDoc(doc);
        if (versionInputRef.current) {
            versionInputRef.current.value = "";
            versionInputRef.current.click();
        }
    };

    // ---------- Modification du type ----------
    const [typeDoc, setTypeDoc] = useState(null);
    const [newType, setNewType] = useState("");
    const [savingType, setSavingType] = useState(false);

    const openTypeModal = (doc) => {
        setTypeDoc(doc);
        setNewType(findType(doc.type)?.value ?? "");
        showModal("typeModal");
    };

    const saveType = async () => {
        if (!typeDoc || !newType) return;
        if (findType(typeDoc.type)?.value === Number(newType)) {
            hideModal("typeModal");
            return;
        }
        setSavingType(true);
        try {
            // Le PUT attend la fiche complète : on renvoie les valeurs actuelles avec le nouveau type
            const d = typeDoc;
            const form = COMMERCIAL_FORMS[docCategory(d)?.value] || COMMERCIAL_FORMS[1];
            await axiosInstance.put(form.item(d.id), {
                id: d.id,
                type: Number(newType),
                numero: d.numero ?? "",
                dateDocument: d.dateDocument,
                montantHT: d.montantHT ?? 0,
                montantTVA: d.montantTVA ?? 0,
                montantTTC: d.montantTTC ?? 0,
                [form.partyField]: d.partyId,
            });
            hideModal("typeModal");
            toast("success", `Type modifié : ${typesFor(docCategory(d)?.value).find((t) => t.value === Number(newType))?.label}`);
            fetchDocuments();
        } catch (error) {
            toast("error", apiMessage(error, "Modification du type échouée"));
        } finally {
            setSavingType(false);
        }
    };

    // ---------- Actions ----------
    const handleDownload = async (doc) => {
        try {
            const res = await axiosInstance.get(API.download(doc.id), { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = doc.fileName || "document";
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast("error", "Téléchargement échoué");
        }
    };

    const handlePreview = async (doc) => {
        // Ouvre l'onglet tout de suite pour éviter le blocage des popups
        const tab = window.open("", "_blank");
        try {
            const res = await axiosInstance.get(API.preview(doc.id), { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data); // le blob garde le Content-Type renvoyé par l'API
            if (tab) tab.location.href = url;
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch {
            if (tab) tab.close();
            toast("error", "Aperçu indisponible pour ce document");
        }
    };

    const handleNewVersion = async (doc, e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            await axiosInstance.post(API.newVersion(doc.id), formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast("success", "Nouvelle version ajoutée");
            fetchDocuments();
        } catch (error) {
            toast("error", apiMessage(error, "Envoi de la version échoué"));
        }
    };

    const confirmAction = (options, action, successMessage) => {
        Swal.fire({ showCancelButton: true, cancelButtonText: "Annuler", ...options })
            .then(async (result) => {
                if (!result.isConfirmed) return;
                try {
                    await action();
                    toast("success", successMessage);
                    fetchDocuments();
                } catch (error) {
                    toast("error", apiMessage(error, "Opération échouée"));
                }
            });
    };

    const handleArchive = (doc) => confirmAction(
        { title: `Archiver « ${doc.fileName} » ?`, icon: "question", confirmButtonText: "Archiver" },
        () => axiosInstance.put(API.archive(doc.id)),
        "Document archivé"
    );

    const handleRestore = (doc) => confirmAction(
        { title: `Restaurer « ${doc.fileName} » ?`, icon: "question", confirmButtonText: "Restaurer" },
        () => axiosInstance.put(API.restore(doc.id)),
        "Document restauré"
    );

    const handleDelete = (doc) => confirmAction(
        { title: `Supprimer « ${doc.fileName} » ?`, text: "Le document sera déplacé dans la corbeille.", icon: "warning", confirmButtonText: "Oui, supprimer" },
        () => axiosInstance.delete(API.documentById(doc.id)),
        "Document supprimé"
    );

    const handleDeletePermanent = (doc) => confirmAction(
        {
            title: `Supprimer définitivement « ${doc.fileName} » ?`,
            text: "Le fichier sera effacé du disque. Cette action est irréversible !",
            icon: "error",
            confirmButtonText: "Supprimer définitivement",
            confirmButtonColor: "#d33",
        },
        () => axiosInstance.delete(API.permanent(doc.id)),
        "Document supprimé définitivement"
    );

    return (
        <div className="container-fluid mt-4">
            <h3 className="text-center my-3">
                {isTrash ? <><i className="fas fa-trash-restore mr-2"></i>Corbeille</> : "Gestion des Documents"}
            </h3>

            <div className="row">
            {/* ---- Arbre des dossiers (filtre) ---- */}
            <div className="col-md-3 mb-3">
                <div className="card">
                    <div className="card-header py-2">
                        <strong>Dossiers</strong>
                    </div>
                    <div className="card-body doc-tree-container">
                        <div
                            className={`doc-tree-node ${!folderFilter ? "active" : ""}`}
                            onClick={() => setFolderFilter("")}
                        >
                            <span className="doc-tree-toggle"></span>
                            <i className="fas fa-layer-group text-secondary mr-2"></i>
                            Tous les dossiers
                        </div>
                        {folderTree.length === 0 ? (
                            <p className="text-muted small mt-2 mb-0">Aucun dossier</p>
                        ) : (
                            <ul className="doc-tree root">
                                {folderTree.map((n) => (
                                    <FolderTreeNode
                                        key={n.id}
                                        node={n}
                                        selectedId={folderFilter}
                                        expanded={expanded}
                                        onToggle={toggleFolder}
                                        onSelect={selectFolder}
                                        counts={docCountByFolder}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-md-9">
            {selectedFolderPath && (
                <div className="mb-2 small">
                    <i className="fas fa-folder-open text-warning mr-1"></i>
                    <strong>{selectedFolderPath}</strong>
                    <button className="btn btn-link btn-sm py-0" onClick={() => setFolderFilter("")}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            )}

            {/* ---- Barre d'outils ---- */}
            <div className="form-row align-items-end mb-3">
                <div className="col-md-5">
                    <label className="small mb-1">Recherche</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Nom, numéro, client, ICE..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {!isTrash && (
                    <div className="col-md-3">
                        <label className="small mb-1">Statut</label>
                        <select className="form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="active">Actifs</option>
                            <option value="archived">Archivés</option>
                            <option value="all">Tous</option>
                        </select>
                    </div>
                )}
                <div className={`${isTrash ? "col-md-7" : "col-md-4"} text-right`}>
                    {isTrash ? (
                        <small className="text-muted">Les documents supprimés peuvent être restaurés ou effacés définitivement.</small>
                    ) : (
                        <button className="btn btn-primary btn-ajouter" onClick={openUpload}>
                            <i className="fas fa-upload"></i> Ajouter des documents
                        </button>
                    )}
                </div>
            </div>

            <div className="form-row align-items-end mb-3">
                <div className="col-md-3">
                    <label className="small mb-1">Catégorie</label>
                    <select className="form-control" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                        <option value="">Toutes les catégories</option>
                        <option value="none">Non classés</option>
                        {DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <div className="col-md-3">
                    <label className="small mb-1">Type de document</label>
                    <select className="form-control" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                        <option value="">Tous les types</option>
                        <option value="simple">Documents simples</option>
                        {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="small mb-1">Du</label>
                    <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                    <label className="small mb-1">Au</label>
                    <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className="col-md-2 text-right">
                    {(search || folderFilter || categoryFilter || typeFilter || dateFrom || dateTo) && (
                        <button
                            className="btn btn-link px-0"
                            onClick={() => { setSearch(""); setFolderFilter(""); setCategoryFilter(""); setTypeFilter(""); setDateFrom(""); setDateTo(""); }}
                        >
                            <i className="fas fa-times mr-1"></i>Réinitialiser les filtres
                        </button>
                    )}
                    <span className="small text-muted d-block">{filteredDocuments.length} document(s)</span>
                </div>
            </div>

            {/* ---- Liste ---- */}
            {loading ? (
                <p>Chargement...</p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover table-sm bg-white documents-table">
                        <thead className="thead-light">
                            <tr>
                                <th>Nom</th>
                                <th>Catégorie</th>
                                <th>Type</th>
                                <th>Dossier</th>
                                <th>Taille</th>
                                <th>Statut</th>
                                <th>Ajouté le</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocuments.length === 0 && (
                                <tr><td colSpan="8" className="text-center text-muted py-4">{isTrash ? "La corbeille est vide" : "Aucun document"}</td></tr>
                            )}
                            {filteredDocuments.map((d) => {
                                const status = STATUS_LABEL(d);
                                const archived = isStatus(d, "archived");
                                const deleted = isStatus(d, "deleted");
                                return (
                                    <tr key={d.id}>
                                        <td>
                                            <i className={`fas ${fileIcon(d.fileName)} mr-2`}></i>
                                            <a href="#!" onClick={(e) => { e.preventDefault(); handlePreview(d); }}>{d.fileName}</a>
                                            {d.description && <div className="small text-muted text-truncate doc-description">{d.description}</div>}
                                        </td>
                                        <td className="small text-nowrap">
                                            {(() => {
                                                const c = docCategory(d);
                                                return c
                                                    ? <span className={`badge ${c.css}`}><i className={`fas ${c.icon} mr-1`}></i>{c.label}</span>
                                                    : <span className="text-muted">Non classé</span>;
                                            })()}
                                        </td>
                                        <td className="small text-nowrap">
                                            {(() => {
                                                if (hasRhInfos(d)) return (
                                                    <span className="text-truncate d-inline-block" style={{ maxWidth: 200 }} title={[d.intitule, d.commentaire].filter(Boolean).join(" — ")}>
                                                        <i className="fas fa-user-tie text-info mr-1"></i>{d.employeNom} • {findTypeRH(d.rhType).label}
                                                        {d.periodeMois ? ` ${MOIS[d.periodeMois - 1]} ${d.periodeAnnee}` : ""}
                                                        {d.rhExpire && <span className="badge badge-danger ml-1">Expiré</span>}
                                                    </span>
                                                );
                                                if (hasProjetInfos(d)) return <span className="text-truncate d-inline-block" style={{ maxWidth: 160 }} title={d.commentaire || ""}><i className="fas fa-hard-hat text-warning mr-1"></i>{d.projetNom}</span>;
                                                const t = typeLabelFor(d);
                                                if (t) return <span className={`badge ${t.css}`}>{t.label}</span>;
                                                if (docCategory(d)) return <span className="text-muted font-italic">à compléter</span>;
                                                return <span className="text-muted">—</span>;
                                            })()}
                                        </td>
                                        <td className="small">{folderPath(folders, d.folderId)}</td>
                                        <td className="small">{formatSize(d.size ?? d.fileSize)}</td>
                                        <td><span className={`badge ${status.css}`}>{status.text}</span></td>
                                        <td className="small">
                                            {d.createdAt ? new Date(d.createdAt).toLocaleDateString("fr-FR") : ""}
                                        </td>
                                        <td className="text-right text-nowrap">
                                            <button className="btn btn-sm btn-outline-info mr-1" title="Aperçu" onClick={() => handlePreview(d)}>
                                                <i className="fas fa-eye"></i>
                                            </button>
                                            <button className="btn btn-sm btn-outline-primary mr-1" title="Télécharger" onClick={() => handleDownload(d)}>
                                                <i className="fas fa-download"></i>
                                            </button>
                                            {deleted ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-success mr-1" title="Restaurer" onClick={() => handleRestore(d)}>
                                                        <i className="fas fa-undo"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger" title="Supprimer définitivement" onClick={() => handleDeletePermanent(d)}>
                                                        <i className="fas fa-times-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                            <button
                                                type="button"
                                                className={`btn btn-sm btn-outline-secondary ${actionMenu?.doc.id === d.id ? "active" : ""}`}
                                                title="Plus"
                                                onClick={(e) => toggleActionMenu(e, d, archived)}
                                            >
                                                <i className="fas fa-ellipsis-v"></i>
                                            </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            </div>
            </div>

            {/* ---- Modal upload ---- */}
            <div className="modal fade" id="uploadModal">
                <div className={`modal-dialog ${isCommercial(uploadFormik.values.kind) ? "modal-lg" : ""}`}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>Ajouter des documents</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            {COMMERCIAL_UPLOAD_ENABLED && (
                            <div className="form-group">
                                <div className="btn-group btn-group-toggle w-100">
                                    {[
                                        { v: "simple", label: "Document simple", icon: "fa-file" },
                                        { v: "commercial", label: "Document commercial", icon: "fa-file-invoice-dollar" },
                                    ].map((k) => (
                                        <button
                                            key={k.v}
                                            type="button"
                                            className={`btn ${uploadFormik.values.kind === k.v ? "btn-primary" : "btn-outline-primary"}`}
                                            onClick={() => uploadFormik.setFieldValue("kind", k.v)}
                                        >
                                            <i className={`fas ${k.icon} mr-2`}></i>{k.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            )}

                            <div className="form-group">
                                <label>Dossier</label>
                                <select
                                    name="folderId"
                                    className={`form-control ${uploadFormik.touched.folderId && uploadFormik.errors.folderId ? "is-invalid" : ""}`}
                                    value={uploadFormik.values.folderId}
                                    onChange={uploadFormik.handleChange}
                                    onBlur={uploadFormik.handleBlur}
                                >
                                    <option value="">-- Sélectionnez --</option>
                                    {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                                </select>
                                <div className="invalid-feedback">{uploadFormik.errors.folderId}</div>
                            </div>

                            {isCommercial(uploadFormik.values.kind) && (
                                <div className="commercial-fields">
                                    <h6 className="text-muted mb-3">Informations du document</h6>
                                    <div className="form-row">
                                        <div className="form-group col-md-4">
                                            <label>Type</label>
                                            <select
                                                name="type"
                                                className={fieldClass("type")}
                                                value={uploadFormik.values.type}
                                                onChange={uploadFormik.handleChange}
                                                onBlur={uploadFormik.handleBlur}
                                            >
                                                <option value="">-- Sélectionnez --</option>
                                                {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                            <div className="invalid-feedback">{uploadFormik.errors.type}</div>
                                        </div>
                                        <div className="form-group col-md-4">{uploadField("numero", "Numéro", { type: "text", placeholder: "ex : FV-2026-0001" })}</div>
                                        <div className="form-group col-md-4">{uploadField("dateDocument", "Date", { type: "date" })}</div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group col-md-4">{uploadField("montantHT", "Montant HT", { type: "number", step: "0.01", onChange: handleAmountChange })}</div>
                                        <div className="form-group col-md-4">{uploadField("montantTVA", "Montant TVA", { type: "number", step: "0.01", onChange: handleAmountChange })}</div>
                                        <div className="form-group col-md-4">{uploadField("montantTTC", "Montant TTC", { type: "number", step: "0.01" })}</div>
                                    </div>

                                    <h6 className="text-muted mb-3">Client / Fournisseur</h6>
                                    <div className="form-row">
                                        <div className="form-group col-md-4">{uploadField("codeClient", "Code", { type: "text" })}</div>
                                        <div className="form-group col-md-8">{uploadField("raisonSociale", "Raison sociale", { type: "text" })}</div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group col-md-8">{uploadField("adresse", "Adresse", { type: "text" })}</div>
                                        <div className="form-group col-md-4">{uploadField("ville", "Ville", { type: "text" })}</div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group col-md-6">{uploadField("telephone", "Téléphone", { type: "tel" })}</div>
                                        <div className="form-group col-md-6">{uploadField("ice", "ICE", { type: "text", maxLength: 15 })}</div>
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label>{isCommercial(uploadFormik.values.kind) ? "Fichier (PDF scanné, etc.)" : "Fichiers"}</label>
                                <div
                                    className={`drop-zone ${uploadFormik.touched.files && uploadFormik.errors.files ? "is-invalid" : ""}`}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        uploadFormik.setFieldTouched("files", true, false);
                                        uploadFormik.setFieldValue("files", [...uploadFormik.values.files, ...Array.from(e.dataTransfer.files)]);
                                    }}
                                >
                                    <i className="fas fa-cloud-upload-alt fa-2x text-muted mb-2"></i>
                                    <div>Glissez vos fichiers ici ou</div>
                                    <label className="btn btn-sm btn-outline-primary mt-2 mb-0">
                                        Parcourir
                                        <input
                                            type="file"
                                            multiple={!isCommercial(uploadFormik.values.kind)}
                                            hidden
                                            onChange={(e) => {
                                                uploadFormik.setFieldTouched("files", true, false);
                                                uploadFormik.setFieldValue("files", [...uploadFormik.values.files, ...Array.from(e.target.files)]);
                                                e.target.value = "";
                                            }}
                                        />
                                    </label>
                                </div>
                                {uploadFormik.touched.files && uploadFormik.errors.files && (
                                    <div className="text-danger small mt-1">{uploadFormik.errors.files}</div>
                                )}
                                {uploadFormik.values.files.length > 0 && (
                                    <ul className="list-group list-group-flush mt-2 small">
                                        {uploadFormik.values.files.map((f, i) => (
                                            <li key={`${f.name}-${i}`} className="list-group-item d-flex justify-content-between align-items-center px-1 py-1">
                                                <span className="text-truncate"><i className={`fas ${fileIcon(f.name)} mr-2`}></i>{f.name}</span>
                                                <span className="text-nowrap">
                                                    <span className="text-muted mr-2">{formatSize(f.size)}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link text-danger p-0"
                                                        onClick={() => uploadFormik.setFieldValue("files", uploadFormik.values.files.filter((_, j) => j !== i))}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    rows={3}
                                    className="form-control"
                                    value={uploadFormik.values.description}
                                    onChange={uploadFormik.handleChange}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal" disabled={uploading}>Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={uploadFormik.handleSubmit} disabled={uploading}>
                                {uploading ? <><i className="fas fa-spinner fa-spin mr-1"></i>Envoi...</> : "Envoyer"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Menu « Plus » flottant (position fixe : jamais coupé par le tableau) ---- */}
            {actionMenu && (
                <div
                    className="dropdown-menu show doc-action-menu"
                    style={{ top: actionMenu.top, bottom: actionMenu.bottom, right: actionMenu.right }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button className="dropdown-item" onClick={() => runMenuAction(openEdit)}>
                        <i className="fas fa-edit mr-2"></i>Modifier
                    </button>
                    <div className="dropdown-divider"></div>
                    {(() => {
                        const doc = actionMenu.doc;
                        const category = docCategory(doc);

                        // Pas encore classé (Aucune / Document) → on choisit d'abord la catégorie
                        if (!category) {
                            return (
                                <button className="dropdown-item font-weight-bold" onClick={() => runMenuAction(openCategoryPicker)}>
                                    <i className="fas fa-folder-plus text-primary mr-2"></i>Définir la catégorie
                                </button>
                            );
                        }

                        return (
                            <>
                                <h6 className="dropdown-header">
                                    <i className={`fas ${category.icon} mr-1`}></i>{category.label}
                                </h6>

                                {COMMERCIAL_FORMS[category.value] && hasCommercialInfos(doc) && (
                                    <>
                                        <button className="dropdown-item" onClick={() => runMenuAction(openCommercial)}>
                                            <i className={`fas ${COMMERCIAL_FORMS[category.value].icon} mr-2`}></i>{COMMERCIAL_FORMS[category.value].infosLabel}
                                        </button>
                                        <button className="dropdown-item" onClick={() => runMenuAction(openTypeModal)}>
                                            <i className="fas fa-tag mr-2"></i>Modifier le type
                                        </button>
                                        <button className="dropdown-item" onClick={() => runMenuAction(handleRemoveCommercial)}>
                                            <i className="fas fa-unlink mr-2"></i>Retirer les infos {COMMERCIAL_FORMS[category.value].of}
                                        </button>
                                    </>
                                )}

                                {category.value === PROJET_CATEGORY && (hasProjetInfos(doc) ? (
                                    <>
                                        <button className="dropdown-item" onClick={() => runMenuAction(openProjetLink)}>
                                            <i className="fas fa-hard-hat mr-2"></i>Infos projet
                                        </button>
                                        <button className="dropdown-item" onClick={() => runMenuAction(handleRemoveProjet)}>
                                            <i className="fas fa-unlink mr-2"></i>Retirer du projet
                                        </button>
                                    </>
                                ) : (
                                    <button className="dropdown-item font-weight-bold" onClick={() => runMenuAction(openProjetLink)}>
                                        <i className="fas fa-plus-circle text-success mr-2"></i>Rattacher à un projet
                                    </button>
                                ))}

                                {COMMERCIAL_FORMS[category.value] && !hasCommercialInfos(doc) && (
                                    <button className="dropdown-item font-weight-bold" onClick={() => runMenuAction(openCommercialCreate)}>
                                        <i className="fas fa-plus-circle text-success mr-2"></i>Compléter les informations
                                    </button>
                                )}

                                {category.value === RH_CATEGORY && (hasRhInfos(doc) ? (
                                    <>
                                        <button className="dropdown-item" onClick={() => runMenuAction(openRhLink)}>
                                            <i className="fas fa-user-tie mr-2"></i>Infos RH
                                        </button>
                                        <button className="dropdown-item" onClick={() => runMenuAction(handleRemoveRh)}>
                                            <i className="fas fa-unlink mr-2"></i>Retirer du dossier employé
                                        </button>
                                    </>
                                ) : (
                                    <button className="dropdown-item font-weight-bold" onClick={() => runMenuAction(openRhLink)}>
                                        <i className="fas fa-plus-circle text-success mr-2"></i>Classer dans un dossier employé
                                    </button>
                                ))}

                                {/* Changer de catégorie : seulement tant qu'aucune info n'est saisie */}
                                {!(COMMERCIAL_FORMS[category.value] && hasCommercialInfos(doc)) && !hasProjetInfos(doc) && !hasRhInfos(doc) && (
                                    <button className="dropdown-item" onClick={() => runMenuAction(openCategoryPicker)}>
                                        <i className="fas fa-exchange-alt mr-2"></i>Changer de catégorie
                                    </button>
                                )}
                            </>
                        );
                    })()}
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item" onClick={() => runMenuAction(pickNewVersion)}>
                        <i className="fas fa-code-branch mr-2"></i>Nouvelle version
                    </button>
                    {actionMenu.archived ? (
                        <button className="dropdown-item" onClick={() => runMenuAction(handleRestore)}>
                            <i className="fas fa-box-open mr-2"></i>Restaurer
                        </button>
                    ) : (
                        <button className="dropdown-item" onClick={() => runMenuAction(handleArchive)}>
                            <i className="fas fa-archive mr-2"></i>Archiver
                        </button>
                    )}
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item text-danger" onClick={() => runMenuAction(handleDelete)}>
                        <i className="fas fa-trash mr-2"></i>Supprimer
                    </button>
                    <button className="dropdown-item text-danger" onClick={() => runMenuAction(handleDeletePermanent)}>
                        <i className="fas fa-times-circle mr-2"></i>Supprimer définitivement
                    </button>
                </div>
            )}

            {/* Input fichier unique pour « Nouvelle version » */}
            <input
                type="file"
                hidden
                ref={versionInputRef}
                onChange={(e) => { if (versionDoc) handleNewVersion(versionDoc, e); }}
            />

            {/* ---- Modal modification du type ---- */}
            <div className="modal fade" id="typeModal">
                <div className="modal-dialog modal-sm">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className="fas fa-tag mr-2"></i>Modifier le type
                                {typeDoc && <small className="text-muted d-block mt-1 text-truncate">{typeDoc.fileName}</small>}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body p-0">
                            <div className="list-group list-group-flush">
                                {typesFor(typeDoc ? docCategory(typeDoc)?.value : 1).map((t) => {
                                    const selected = Number(newType) === t.value;
                                    const current = findType(typeDoc?.type)?.value === t.value;
                                    return (
                                        <label
                                            key={t.value}
                                            className={`list-group-item list-group-item-action d-flex align-items-center mb-0 ${selected ? "active-type" : ""}`}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <input
                                                type="radio"
                                                name="docType"
                                                className="mr-3"
                                                value={t.value}
                                                checked={selected}
                                                onChange={(e) => setNewType(e.target.value)}
                                            />
                                            <span className={`badge ${t.css} mr-2`}>&nbsp;</span>
                                            {t.label}
                                            {current && <small className="text-muted ml-auto">actuel</small>}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal" disabled={savingType}>Annuler</button>
                            <button
                                type="button"
                                className="btn btn-enregistrer"
                                onClick={saveType}
                                disabled={!newType || savingType}
                            >
                                {savingType ? <><i className="fas fa-spinner fa-spin mr-1"></i>Enregistrement...</> : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal choix de la catégorie ---- */}
            <div className="modal fade" id="categoryModal">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className="fas fa-folder-plus mr-2"></i>Définir la catégorie
                                {categoryDoc && <small className="text-muted d-block mt-1 text-truncate">{categoryDoc.fileName}</small>}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <p className="text-muted small mb-3">
                                Quel type de document est-ce ? Vous pourrez ensuite compléter ses informations depuis le menu ⋮.
                            </p>
                            <div className="list-group">
                                {DOC_CATEGORIES.map((c) => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        className={`list-group-item list-group-item-action d-flex align-items-center category-choice ${categoryDoc && docCategory(categoryDoc)?.value === c.value ? "active-type" : ""}`}
                                        disabled={savingCategory}
                                        onClick={() => chooseCategory(c)}
                                    >
                                        <i className={`fas ${c.icon} ${c.color} fa-lg mr-3`} style={{ width: 24 }}></i>
                                        <span className="flex-grow-1 text-left">
                                            <strong>{c.label}</strong>
                                            <small className="d-block text-muted">{c.hint}</small>
                                        </span>
                                        {categoryDoc && docCategory(categoryDoc)?.value === c.value
                                            ? <small className="text-muted">actuelle</small>
                                            : !c.hasForm && <span className="badge badge-light" title="Formulaire de saisie bientôt disponible">Saisie bientôt</span>}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal informations commerciales ---- */}
            <div className="modal fade" id="commercialModal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className={`fas ${commercialForm.icon} mr-2`}></i>
                                {commercialMode === "create"
                                    ? `Compléter : informations ${commercialForm.of}`
                                    : `Informations ${commercialForm.of}`}
                                {commercialDoc && <small className="text-muted d-block mt-1">{commercialDoc.fileName}</small>}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            {commercialLoading && (
                                <div className="small text-muted mb-2"><i className="fas fa-spinner fa-spin mr-1"></i>Chargement...</div>
                            )}

                            <h6 className="text-muted mb-3">Document</h6>
                            <div className="form-row">
                                <div className="form-group col-md-4">
                                    <label>Type</label>
                                    <select
                                        name="type"
                                        className={`form-control ${commercialFormik.touched.type && commercialFormik.errors.type ? "is-invalid" : ""}`}
                                        value={commercialFormik.values.type}
                                        onChange={commercialFormik.handleChange}
                                        onBlur={commercialFormik.handleBlur}
                                    >
                                        <option value="">-- Sélectionnez --</option>
                                        {typesFor(commercialCategory).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    <div className="invalid-feedback">{commercialFormik.errors.type}</div>
                                </div>
                                <div className="form-group col-md-4">{renderField(commercialFormik, "numero", "Numéro", { type: "text" })}</div>
                                <div className="form-group col-md-4">{renderField(commercialFormik, "dateDocument", "Date", { type: "date" })}</div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-4">{renderField(commercialFormik, "montantHT", "Montant HT", { type: "number", step: "0.01", onChange: handleCommercialAmountChange })}</div>
                                <div className="form-group col-md-4">{renderField(commercialFormik, "montantTVA", "Montant TVA", { type: "number", step: "0.01", onChange: handleCommercialAmountChange })}</div>
                                <div className="form-group col-md-4">{renderField(commercialFormik, "montantTTC", "Montant TTC", { type: "number", step: "0.01" })}</div>
                            </div>

                            <h6 className="text-muted mb-2">{commercialForm.party}</h6>
                            <div className="form-row">
                                <div className="form-group col-md-6">
                                    <select
                                        name="partyId"
                                        className={`form-control ${commercialFormik.touched.partyId && commercialFormik.errors.partyId ? "is-invalid" : ""}`}
                                        value={commercialFormik.values.partyId}
                                        onChange={commercialFormik.handleChange}
                                        onBlur={commercialFormik.handleBlur}
                                        disabled={!parties[commercialForm.partyList]}
                                    >
                                        <option value="">
                                            {parties[commercialForm.partyList] ? `-- Sélectionnez le ${commercialForm.party.toLowerCase()} --` : "Chargement..."}
                                        </option>
                                        {partyOptions.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                                    </select>
                                    <div className="invalid-feedback">{commercialFormik.errors.partyId}</div>
                                </div>
                                <div className="form-group col-md-6">
                                    {selectedParty && (
                                        <div className="tiers-readonly small">
                                            {selectedParty.contactName && <div><i className="fas fa-user mr-1"></i>{selectedParty.contactName}</div>}
                                            {selectedParty.phoneNumber && <div><i className="fas fa-phone mr-1"></i>{selectedParty.phoneNumber}</div>}
                                            {selectedParty.email && <div><i className="fas fa-envelope mr-1"></i>{selectedParty.email}</div>}
                                            {selectedParty.address && <div><i className="fas fa-map-marker-alt mr-1"></i>{selectedParty.address}</div>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button
                                type="button"
                                className="btn btn-enregistrer"
                                onClick={commercialFormik.handleSubmit}
                                disabled={commercialLoading || commercialFormik.isSubmitting}
                            >
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal rattachement à un projet ---- */}
            <div className="modal fade" id="projetLinkModal">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className="fas fa-hard-hat text-warning mr-2"></i>
                                {projetMode === "create" ? "Rattacher à un projet" : "Infos projet"}
                                {projetDoc && <small className="text-muted d-block mt-1 text-truncate">{projetDoc.fileName}</small>}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Projet</label>
                                <select
                                    name="projetId"
                                    className={`form-control ${projetFormik.touched.projetId && projetFormik.errors.projetId ? "is-invalid" : ""}`}
                                    value={projetFormik.values.projetId}
                                    onChange={projetFormik.handleChange}
                                    onBlur={projetFormik.handleBlur}
                                    disabled={!projets}
                                >
                                    <option value="">{projets ? "-- Sélectionnez le projet --" : "Chargement..."}</option>
                                    {(projets || []).map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.nom}{p.clientName ? ` — ${p.clientName}` : ""}{p.statut === 2 || p.statut === "Termine" ? " (terminé)" : ""}
                                        </option>
                                    ))}
                                </select>
                                <div className="invalid-feedback">{projetFormik.errors.projetId}</div>
                                {projetHint && projetMode === "create" && (
                                    <small className="form-text text-muted">
                                        <i className="fas fa-magic mr-1"></i>Repris du dernier document du dossier : {projetHint.projetNom}
                                    </small>
                                )}
                                {projets && projets.length === 0 && (
                                    <small className="form-text text-muted">Aucun projet : créez-le d'abord dans Archives → Projets.</small>
                                )}
                            </div>
                            <div className="form-group mb-0">
                                <label>Commentaire</label>
                                <textarea
                                    name="commentaire"
                                    rows={3}
                                    className={`form-control ${projetFormik.errors.commentaire ? "is-invalid" : ""}`}
                                    placeholder="Ex. : façade après ravalement, vue côté jardin…"
                                    value={projetFormik.values.commentaire}
                                    onChange={projetFormik.handleChange}
                                />
                                <div className="invalid-feedback">{projetFormik.errors.commentaire}</div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button
                                type="button"
                                className="btn btn-enregistrer"
                                onClick={projetFormik.handleSubmit}
                                disabled={projetFormik.isSubmitting}
                            >
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal document RH ---- */}
            <div className="modal fade" id="rhLinkModal">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className="fas fa-user-tie text-info mr-2"></i>
                                {rhMode === "create" ? "Classer dans un dossier employé" : "Infos RH"}
                                {rhDoc && <small className="text-muted d-block mt-1 text-truncate">{rhDoc.fileName}</small>}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group col-md-6">
                                    <label>Employé</label>
                                    <select name="employeId" disabled={!employes}
                                        className={`form-control ${rhFormik.touched.employeId && rhFormik.errors.employeId ? "is-invalid" : ""}`}
                                        value={rhFormik.values.employeId} onChange={rhFormik.handleChange} onBlur={rhFormik.handleBlur}>
                                        <option value="">{employes ? "-- Sélectionnez --" : "Chargement..."}</option>
                                        {(employes || []).map((e) => (
                                            <option key={e.id} value={e.id}>
                                                {e.matricule ? `${e.matricule} — ` : ""}{e.nom} {e.prenom || ""}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="invalid-feedback">{rhFormik.errors.employeId}</div>
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Type de document</label>
                                    <select name="type"
                                        className={`form-control ${rhFormik.touched.type && rhFormik.errors.type ? "is-invalid" : ""}`}
                                        value={rhFormik.values.type} onChange={rhFormik.handleChange} onBlur={rhFormik.handleBlur}>
                                        <option value="">-- Sélectionnez --</option>
                                        {TYPES_RH.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    <div className="invalid-feedback">{rhFormik.errors.type}</div>
                                </div>
                            </div>

                            {rhShow("periode") && (
                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label>Mois de paie</label>
                                        <select name="periodeMois"
                                            className={`form-control ${rhFormik.errors.periodeMois ? "is-invalid" : ""}`}
                                            value={rhFormik.values.periodeMois} onChange={rhFormik.handleChange}>
                                            <option value="">--</option>
                                            {MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                        </select>
                                        <div className="invalid-feedback">{rhFormik.errors.periodeMois}</div>
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label>Année</label>
                                        <input type="number" name="periodeAnnee" min="1990" max="2100"
                                            className={`form-control ${rhFormik.errors.periodeAnnee ? "is-invalid" : ""}`}
                                            value={rhFormik.values.periodeAnnee} onChange={rhFormik.handleChange} />
                                        <div className="invalid-feedback">{rhFormik.errors.periodeAnnee}</div>
                                    </div>
                                </div>
                            )}

                            {rhShow("intitule") && (
                                <div className="form-group">
                                    <label>Intitulé</label>
                                    <input name="intitule" className={`form-control ${rhFormik.errors.intitule ? "is-invalid" : ""}`}
                                        placeholder={INTITULE_PLACEHOLDER[Number(rhFormik.values.type)] || ""}
                                        value={rhFormik.values.intitule} onChange={rhFormik.handleChange} />
                                    <div className="invalid-feedback">{rhFormik.errors.intitule}</div>
                                </div>
                            )}

                            {rhShow("organisme") && (
                                <div className="form-group">
                                    <label>Établissement / organisme</label>
                                    <input name="organisme" className={`form-control ${rhFormik.errors.organisme ? "is-invalid" : ""}`}
                                        value={rhFormik.values.organisme} onChange={rhFormik.handleChange} />
                                    <div className="invalid-feedback">{rhFormik.errors.organisme}</div>
                                </div>
                            )}

                            {(rhShow("dateDocument") || rhShow("dateExpiration")) && (
                                <div className="form-row">
                                    {rhShow("dateDocument") && (
                                        <div className="form-group col-md-6">
                                            <label>{Number(rhFormik.values.type) === 8 ? "Du" : "Date du document"}</label>
                                            <input type="date" name="dateDocument" className="form-control"
                                                value={rhFormik.values.dateDocument} onChange={rhFormik.handleChange} />
                                        </div>
                                    )}
                                    {rhShow("dateExpiration") && (
                                        <div className="form-group col-md-6">
                                            <label>{Number(rhFormik.values.type) === 8 ? "Au" : "Date d'expiration / de fin"}</label>
                                            <input type="date" name="dateExpiration" className="form-control"
                                                value={rhFormik.values.dateExpiration} onChange={rhFormik.handleChange} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {rhFormik.values.type && (
                                <div className="form-group mb-0">
                                    <label>Commentaire</label>
                                    <textarea name="commentaire" rows={2}
                                        className={`form-control ${rhFormik.errors.commentaire ? "is-invalid" : ""}`}
                                        value={rhFormik.values.commentaire} onChange={rhFormik.handleChange} />
                                    <div className="invalid-feedback">{rhFormik.errors.commentaire}</div>
                                </div>
                            )}
                            {employes && employes.length === 0 && (
                                <small className="form-text text-muted">Aucun employé : créez-le d'abord dans Gestion de projets → Employés.</small>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer"
                                onClick={rhFormik.handleSubmit} disabled={rhFormik.isSubmitting}>
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal modification ---- */}
            <div className="modal fade" id="editModal">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>Modifier le document</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Nom</label>
                                <input
                                    type="text"
                                    name="fileName"
                                    className={`form-control ${editFormik.touched.fileName && editFormik.errors.fileName ? "is-invalid" : ""}`}
                                    value={editFormik.values.fileName}
                                    onChange={editFormik.handleChange}
                                    onBlur={editFormik.handleBlur}
                                />
                                <div className="invalid-feedback">{editFormik.errors.fileName}</div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    rows={3}
                                    className="form-control"
                                    value={editFormik.values.description}
                                    onChange={editFormik.handleChange}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={editFormik.handleSubmit}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Documents;